import React from 'react';
import { Avatar } from './components.jsx';
import { CATEGORIES, USERS } from './data.jsx';
// Stats dashboard

const StatsView = ({ state }) => {
  // Dedicated fetch - pagination broke the previous "derive from
  // state.tickets" approach because state.tickets now holds only the
  // current tickets-list page (max 50 rows). The Dashboard needs the
  // full set to compute open / resolved / per-user / per-category /
  // 14-day series correctly. Pulls up to 2000 rows in one shot; for
  // helpdesks approaching that, we should switch to a dedicated
  // /api/stats endpoint that computes aggregates server-side.
  const [tickets, setTickets] = React.useState([]);
  React.useEffect(() => {
    window.IT_AUTH.api('GET', '/tickets?view=all&size=2000', undefined, 'stats')
      .then(r => { if (Array.isArray(r?.rows)) setTickets(r.rows); })
      .catch(err => console.warn('[stats] fetch failed:', err.message));
  }, []);
  const open = tickets.filter(t => t.status !== "resolved").length;
  const resolved = tickets.filter(t => t.status === "resolved").length;
  const unassigned = tickets.filter(t => !t.assignee && t.status !== "resolved").length;
  const urgent = tickets.filter(t => t.priority === "urgent" && t.status !== "resolved").length;

  // Tickets by assignee (open + resolved)
  const byAgent = USERS.map(u => ({
    user: u,
    open: tickets.filter(t => t.assignee === u.id && t.status !== "resolved").length,
    resolved: tickets.filter(t => t.assignee === u.id && t.status === "resolved").length,
  }));
  const maxAgent = Math.max(1, ...byAgent.map(a => a.open + a.resolved));

  // Tickets by category
  const byCat = CATEGORIES.map(c => ({
    cat: c,
    total: tickets.filter(t => t.category === c.id).length
  })).sort((a,b) => b.total - a.total);
  const maxCat = Math.max(1, ...byCat.map(c => c.total));

  // Real tickets per day for last 14 days
  const now = Date.now();
  const dailyData = Array.from({ length: 14 }, (_, i) => {
    const dayStart = now - (14 - i) * 86400000;
    const dayEnd   = now - (13 - i) * 86400000;
    return tickets.filter(t => {
      const ms = now + (t.created || 0) * 60000;
      return ms >= dayStart && ms < dayEnd;
    }).length;
  });
  const maxDaily = Math.max(1, ...dailyData);

  // Real resolved in last 14 days
  const resolved14 = tickets.filter(t => {
    if (t.status !== "resolved" || t.closedAt == null) return false;
    const ms = now + (t.closedAt || 0) * 60000;
    return ms >= now - 14 * 86400000;
  }).length;

  // Avg resolution time (minutes) for resolved tickets with both created + closedAt
  const resTimes = tickets
    .filter(t => t.status === "resolved" && t.closedAt != null && t.created != null)
    .map(t => Math.abs((t.closedAt || 0) - (t.created || 0)));
  const avgResMin = resTimes.length
    ? Math.round(resTimes.reduce((a, b) => a + b, 0) / resTimes.length)
    : null;
  const fmtDuration = (m) => m == null ? "-" : m < 60 ? `${m}m` : m < 1440 ? `${Math.round(m/60)}h` : `${Math.round(m/1440)}d`;

  // Day labels for x-axis
  const dayLabels = [13, 6, 0].map(d => {
    const dt = new Date(now - d * 86400000);
    return `${dt.getDate()} ${dt.toLocaleString("default", { month: "short" })}`;
  });

  return (
    <div className="stats">
      <h2 style={{margin:"0 0 4px", fontSize:18, fontWeight:600, letterSpacing:"-0.01em"}}>Helpdesk overview</h2>
      <div style={{fontSize:12, color:"var(--text-subtle)", marginBottom: 20}}>Last 14 days · all agents</div>

      <div className="stat-grid">
        <div className="stat-card">
          <div className="label">OPEN TICKETS</div>
          <div className="val">{open}</div>
          <div className="trend">{open === 0 ? "All clear" : `${unassigned} unassigned`}</div>
        </div>
        <div className="stat-card">
          <div className="label">RESOLVED (14d)</div>
          <div className="val">{resolved14}</div>
          <div className="trend up">closed in last 14 days</div>
        </div>
        <div className="stat-card">
          <div className="label">UNASSIGNED</div>
          <div className="val">{unassigned}</div>
          <div className="trend">{unassigned > 0 ? "Needs grabbing" : "All claimed"}</div>
        </div>
        <div className="stat-card">
          <div className="label">URGENT OPEN</div>
          <div className="val">{urgent}</div>
          <div className="trend">{urgent > 0 ? "Attention required" : "None"}</div>
        </div>
      </div>

      <div className="charts-row">
        <div className="chart-card">
          <h3>Ticket volume - last 14 days</h3>
          <div className="area-wrap">
            <svg viewBox="0 0 700 200" width="100%" height="200" preserveAspectRatio="none">
              {/* gridlines */}
              {[0, 50, 100, 150].map(y => (
                <line key={y} x1="0" x2="700" y1={y+10} y2={y+10} stroke="oklch(92% 0.005 80)" strokeDasharray="2 4"/>
              ))}
              {/* bars (line+area) */}
              {(() => {
                const w = 700 / dailyData.length;
                const pts = dailyData.map((v, i) => [w * (i + 0.5), 180 - (v / maxDaily) * 160 + 10]);
                const linePath = pts.map((p,i) => `${i ? "L" : "M"}${p[0]},${p[1]}`).join(" ");
                const areaPath = linePath + ` L${pts[pts.length-1][0]},190 L${pts[0][0]},190 Z`;
                return (
                  <>
                    <path d={areaPath} fill="oklch(92% 0.06 264)" opacity="0.5"/>
                    <path d={linePath} fill="none" stroke="oklch(52% 0.16 264)" strokeWidth="2"/>
                    {pts.map((p,i) => (
                      <circle key={i} cx={p[0]} cy={p[1]} r="3" fill="white" stroke="oklch(52% 0.16 264)" strokeWidth="1.5"/>
                    ))}
                  </>
                );
              })()}
            </svg>
            <div style={{display:"flex", justifyContent:"space-between", fontSize:10, color:"var(--text-subtle)", fontFamily:"var(--mono)", marginTop:6, padding:"0 12px"}}>
              {dayLabels.map((l,i) => <span key={i}>{l}</span>)}
            </div>
          </div>
        </div>

        <div className="chart-card">
          <h3>Avg. resolution time</h3>
          <div style={{display:"flex", alignItems:"baseline", gap:10, marginBottom: 20}}>
            <span style={{fontSize: 36, fontWeight: 600, letterSpacing:"-0.02em", fontVariantNumeric:"tabular-nums"}}>{fmtDuration(avgResMin)}</span>
            <span style={{fontSize:12, color:"var(--text-subtle)"}}>{resTimes.length} resolved ticket{resTimes.length !== 1 ? "s" : ""}</span>
          </div>
          <div style={{fontSize:11, color:"var(--text-subtle)", marginBottom:6, letterSpacing:".02em"}}>RESOLUTION BY PRIORITY</div>
          {["urgent","high","medium","low"].map(pri => {
            const pts = tickets.filter(t => t.priority === pri && t.status === "resolved" && t.closedAt != null && t.created != null);
            const avg = pts.length ? Math.round(pts.reduce((a,t) => a + Math.abs((t.closedAt||0)-(t.created||0)), 0) / pts.length) : null;
            const maxP = Math.max(1, ...["urgent","high","medium","low"].map(p => {
              const pp = tickets.filter(t => t.priority === p && t.status === "resolved" && t.closedAt != null && t.created != null);
              return pp.length ? Math.round(pp.reduce((a,t) => a + Math.abs((t.closedAt||0)-(t.created||0)), 0) / pp.length) : 0;
            }));
            const pct = avg != null ? Math.round((avg / maxP) * 100) : 0;
            return (
              <div key={pri} style={{display:"grid", gridTemplateColumns:"60px 1fr 48px", gap:8, alignItems:"center", padding:"5px 0"}}>
                <span style={{fontSize:11, color:"var(--text-muted)", textTransform:"capitalize"}}>{pri}</span>
                <div className="bar-track"><div className="bar-fill" style={{width: pct+"%", background:`var(--pri-${pri === "medium" ? "med" : pri})`}}/></div>
                <span style={{fontFamily:"var(--mono)", fontSize:11, textAlign:"right"}}>{avg != null ? fmtDuration(avg) : "-"}</span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="charts-row">
        <div className="chart-card">
          <h3>Tickets per person</h3>
          {byAgent.map(a => (
            <div key={a.user.id} className="bar-row">
              <div className="name" style={{display:"flex", alignItems:"center", gap:6}}>
                <Avatar user={a.user} size="sm"/>
                <span>{a.user.name.split(" ")[0]}</span>
              </div>
              <div style={{display:"flex", height:"6px", borderRadius:3, overflow:"hidden", background:"var(--bg-sub)"}}>
                <div style={{width: (a.open / maxAgent * 100)+"%", background:"var(--accent)"}}/>
                <div style={{width: (a.resolved / maxAgent * 100)+"%", background:"oklch(85% 0.04 264)"}}/>
              </div>
              <div className="num">{a.open + a.resolved}</div>
            </div>
          ))}
          <div style={{marginTop:14, display:"flex", gap:14, fontSize:11, color:"var(--text-muted)"}}>
            <span style={{display:"flex",alignItems:"center",gap:6}}><span style={{width:10,height:10,background:"var(--accent)",borderRadius:2}}/>Open</span>
            <span style={{display:"flex",alignItems:"center",gap:6}}><span style={{width:10,height:10,background:"oklch(85% 0.04 264)",borderRadius:2}}/>Resolved</span>
          </div>
        </div>

        <div className="chart-card">
          <h3>By category</h3>
          {byCat.map(c => (
            <div key={c.cat.id} className="bar-row">
              <div className="name">{c.cat.label}</div>
              <div className="bar-track">
                <div className="bar-fill" style={{width: (c.total / maxCat * 100) + "%"}}/>
              </div>
              <div className="num">{c.total}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export { StatsView };